import { useRef, useState } from "react";
import { Page, useCurrent } from "./common";
import { PageHead, StatusBadge, Plate, Modal } from "../components/ui";
import { Icon } from "../components/Icons";
import { actions, money, BRIEF_TEMPLATE } from "../lib/store";
import { useCan, useSession } from "../lib/auth";
import { BriefChecklistPanel } from "../components/BriefChecklist";
import { downloadSpaceReportPdf } from "../lib/pdf";
import { draftBrief } from "../lib/ai";
import { fileToDataUri } from "../lib/image";
import { briefDoc } from "../lib/pdf";
import { PdfPreview } from "../components/PdfPreview";
import { RecordBadge } from "../components/RecordStatus";
import { openWhatsApp } from "../lib/whatsapp";
import { STUDIO } from "../lib/store";
import { totalArea, unitLabel, newSpace, zoneFor, BUILDING_TYPES } from "../lib/program";
import { fmtDate } from "../lib/format";
import type { SpaceReq } from "../lib/types";

const rawDataUrl = (file: File) => new Promise<string>((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result as string); r.onerror = rej; r.readAsDataURL(file); });

const ZONES = ["Public", "Private", "Service", "Circulation", "Outdoor"] as const;
const ZONE_COLOR: Record<string, string> = { Public: "var(--sky)", Private: "var(--accent)", Service: "var(--sage)", Circulation: "var(--ink-3)", Outdoor: "var(--gold)" };

/** Set one space's area/unit; in "lock" mode, absorb the delta across the other
 *  spaces proportionally so the gross total stays constant. */
function scaleSpaces(spaces: SpaceReq[], id: string, newArea: number, lock: boolean): SpaceReq[] {
  const idx = spaces.findIndex((s) => s.id === id);
  if (idx < 0) return spaces;
  const next = spaces.map((s) => ({ ...s }));
  const s = next[idx];
  const delta = s.qty * (Math.max(0, newArea) - s.area); // added area (before redistribution)
  s.area = Math.max(0, Math.round(newArea));
  if (!lock || delta === 0) return next;
  const others = next.filter((_, i) => i !== idx);
  const otherTotal = others.reduce((n, x) => n + x.qty * x.area, 0);
  if (otherTotal <= 0) return next;
  for (let i = 0; i < next.length; i++) {
    if (i === idx || next[i].qty <= 0) continue;
    const sub = next[i].qty * next[i].area;
    const newSub = Math.max(next[i].qty * 20, sub - delta * (sub / otherTotal)); // floor 20/unit
    next[i].area = Math.round(newSub / next[i].qty);
  }
  return next;
}

export default function Brief() {
  const p = useCurrent();
  const canEdit = useCan("brief");
  const session = useSession();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(() => p?.brief.sections.map((s) => ({ ...s })) ?? []);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMsg, setAiMsg] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [refOpen, setRefOpen] = useState(false);
  const [guidance, setGuidance] = useState("");
  const [scanOpen, setScanOpen] = useState(false);
  const [notes, setNotes] = useState("");
  const [scanFile, setScanFile] = useState<string | null>(null);
  const [scanFileName, setScanFileName] = useState("");
  const [pdfOpen, setPdfOpen] = useState(false);
  const [sectionBusy, setSectionBusy] = useState<string | null>(null);
  const [scaleMode, setScaleMode] = useState<"lock" | "grow">("lock");
  const [work, setWork] = useState<SpaceReq[] | null>(null);
  const scanFileRef = useRef<HTMLInputElement>(null);
  if (!p) return null;
  const b = p.brief;
  const refs = b.references ?? [];
  const spaces = b.spaces ?? [];
  const unit = b.areaUnit ?? "sqft";

  function save() { actions.updateBrief(p!.id, draft); setEditing(false); }
  function startEdit() { setDraft(b.sections.map((s) => ({ ...s }))); setEditing(true); }

  // ---- section builder ----
  const sid = () => "bs-" + Math.random().toString(36).slice(2, 9);
  const addSection = (title = "New section", hint?: string) => setDraft((d) => [...d, { id: sid(), title, body: "", hint }]);
  const removeSection = (id: string) => setDraft((d) => d.filter((x) => x.id !== id));
  const moveSection = (id: string, dir: -1 | 1) => setDraft((d) => {
    const i = d.findIndex((x) => x.id === id); const j = i + dir;
    if (i < 0 || j < 0 || j >= d.length) return d;
    const next = [...d]; [next[i], next[j]] = [next[j], next[i]]; return next;
  });
  const suggestions = BRIEF_TEMPLATE.filter((t) => !draft.some((s) => s.title.toLowerCase() === t.title.toLowerCase()));

  const proj = () => ({ name: p!.name, type: p!.type, types: p!.types, location: p!.location, area: p!.area, gia: p!.gia, giaUnit: p!.giaUnit, budget: p!.budget, floors: p!.floors });
  function aiFail(res: { reason?: string }) {
    setAiMsg(res.reason === "no-key" ? "The studio mind isn't enabled yet — set ANTHROPIC_API_KEY on the worker." : "Couldn't reach the studio mind. You can still write the brief manually.");
  }

  async function aiDraft(opts: { notes?: string; fileData?: string } = {}) {
    setAiBusy(true); setAiMsg(null);
    const fromSource = !!(opts.notes || opts.fileData);
    const base = editing ? draft : b.sections.map((s) => ({ ...s }));
    const res = await draftBrief(proj(), base.map((s) => ({ title: s.title, body: s.body })), { instruction: guidance.trim() || undefined, notes: opts.notes, fileData: opts.fileData });
    setAiBusy(false);
    if (!res.ok) return aiFail(res);
    if (res.sections?.length) {
      const byTitle = new Map(res.sections.map((s) => [s.title.toLowerCase().trim(), s.body]));
      setDraft(base.map((s) => ({ ...s, body: fromSource ? (byTitle.get(s.title.toLowerCase().trim()) ?? s.body) : (s.body?.trim() ? s.body : (byTitle.get(s.title.toLowerCase().trim()) ?? s.body)) })));
      setEditing(true);
    }
    if (res.spaces?.length) {
      actions.setBriefSpaces(p!.id, res.spaces.map((s) => ({ id: newSpace().id, name: s.name, qty: s.qty || 1, area: Math.round(s.area || 0), zone: (s.zone as any) || zoneFor(s.name), note: s.note })));
      setAiMsg(`AI ${fromSource ? "read your input and researched" : "researched"} a ${res.spaces.length}-space program — review the sizing & distribution below.`);
    }
  }

  async function draftSection(id: string) {
    const cur = editing ? draft : b.sections.map((s) => ({ ...s }));
    const sec = cur.find((s) => s.id === id); if (!sec) return;
    setSectionBusy(id); setAiMsg(null);
    const res = await draftBrief(proj(), cur.map((s) => ({ title: s.title, body: s.body })), { instruction: guidance.trim() || undefined, only: sec.title });
    setSectionBusy(null);
    if (!res.ok) return aiFail(res);
    const hit = res.sections?.find((s) => s.title.toLowerCase().trim() === sec.title.toLowerCase().trim()) ?? res.sections?.[0];
    if (hit) { setDraft(cur.map((s) => s.id === id ? { ...s, body: hit.body } : s)); setEditing(true); }
  }

  async function onScanFile(file: File | null) {
    if (!file) return;
    if (file.type.startsWith("text/") || /\.(txt|md|csv)$/i.test(file.name)) {
      try { const text = await file.text(); setNotes((n) => (n ? n + "\n\n" : "") + text); setScanFile(null); setScanFileName(file.name); } catch { setScanFileName("Couldn't read that file."); }
    } else if (file.type === "application/pdf" || file.type.startsWith("image/")) {
      try {
        const uri = file.type.startsWith("image/") ? await fileToDataUri(file, 1600, 0.8) : await rawDataUrl(file);
        if (uri.length > 6_500_000) { setScanFileName(`${file.name} — too large (max ~4MB).`); setScanFile(null); return; }
        setScanFile(uri); setScanFileName(file.name);
      } catch { setScanFileName("Couldn't read that file."); }
    } else {
      setScanFileName(`${file.name} — unsupported. Use a PDF, image, or text file.`); setScanFile(null);
    }
  }
  function runScan() {
    const n = notes.trim();
    if (!n && !scanFile) return;
    setScanOpen(false);
    aiDraft({ notes: n || undefined, fileData: scanFile || undefined });
    setNotes(""); setScanFile(null); setScanFileName("");
  }

  function copyIntake() {
    navigator.clipboard?.writeText(`${location.origin}/portal/${p!.id}/brief`);
    setCopied(true); setTimeout(() => setCopied(false), 2200);
  }

  // ---- space editing (direct persist) ----
  const setSpaces = (next: SpaceReq[]) => actions.setBriefSpaces(p.id, next);
  const updSpace = (id: string, patch: Partial<SpaceReq>) => setSpaces(spaces.map((s) => s.id === id ? { ...s, ...patch } : s));

  return (
    <Page max={900}>
      <PageHead kicker={`${p.name} · Engagement`} title="Design Brief">
        <StatusBadge status={b.status} />
        <button className="btn ghost sm" onClick={() => setPdfOpen(true)}><Icon.report width={15} />Preview PDF</button>
        {canEdit && !editing && b.status !== "approved" && <button className="btn ghost sm" onClick={startEdit}><Icon.edit width={15} />Edit</button>}
        {canEdit && !editing && (b.status === "draft" || b.status === "changes") && <button className="btn accent sm" onClick={() => actions.sendBrief(p.id)}><Icon.send width={15} />Send for approval</button>}
        {editing && <><button className="btn ghost sm" onClick={() => setEditing(false)}>Cancel</button><button className="btn sm" onClick={save}><Icon.check width={15} />Save</button></>}
      </PageHead>

      {/* project facts */}
      <div className="card pad reveal" style={{ marginBottom: 18 }}>
        <div className="kicker" style={{ marginBottom: 12 }}>Project facts</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px,1fr))", gap: 16 }}>
          <Fact label="Building type" value={p.type || "—"} />
          <Fact label="Location" value={p.location || "—"} icon="pin" />
          <Fact label="Floor area" value={p.area || "—"} />
          <Fact label="Budget" value={money(p.budget, p.currency)} />
          <div>
            <div className="kicker" style={{ fontSize: 9.5, marginBottom: 5 }}>Survey plan</div>
            {canEdit ? (
              <select className="select" style={{ padding: "6px 8px", fontSize: 13 }} value={p.surveyPlan ?? "unknown"} onChange={(e) => actions.setSurvey(p.id, e.target.value as any)}>
                <option value="yes">Available</option><option value="no">Not available</option><option value="unknown">To confirm</option>
              </select>
            ) : <div style={{ fontWeight: 500 }}>{p.surveyPlan === "yes" ? "Available" : p.surveyPlan === "no" ? "Not available" : "To confirm"}</div>}
          </div>
        </div>
        {p.geo && (
          <div style={{ marginTop: 14, borderRadius: 12, overflow: "hidden", border: "1px solid var(--line)" }}>
            <iframe title="Location" width="100%" height="180" style={{ border: 0, display: "block" }} loading="lazy"
              src={`https://www.openstreetmap.org/export/embed.html?bbox=${p.geo.lng - 0.02}%2C${p.geo.lat - 0.012}%2C${p.geo.lng + 0.02}%2C${p.geo.lat + 0.012}&layer=mapnik&marker=${p.geo.lat}%2C${p.geo.lng}`} />
          </div>
        )}
      </div>

      {/* client intake link */}
      {canEdit && (
        <div className="panel row between reveal" style={{ padding: "12px 16px", marginBottom: 18, gap: 12 }}>
          <div className="row" style={{ gap: 10 }}><Icon.link width={16} style={{ color: "var(--accent-ink)" }} />
            <span style={{ fontSize: 13.5 }}>Share a link so <b>{p.client.name}</b> can build out the brief — client code <span className="mono badge accent" style={{ fontSize: 10.5 }}>{p.client.code}</span></span>
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button className="btn ghost sm" style={{ color: "#25D366", borderColor: "#25D366" }} onClick={() => openWhatsApp(`${STUDIO.name} — your project portal for ${p.name}: ${location.origin}/portal/${p.id}  ·  access code: ${p.client.code}`)}><Icon.whatsapp width={15} />Share</button>
            <button className="btn soft sm" onClick={copyIntake}>{copied ? <><Icon.check width={14} />Copied</> : <><Icon.link width={14} />Copy link</>}</button>
          </div>
        </div>
      )}
      {/* AI assist — guided draft + scan client notes */}
      {canEdit && b.status !== "approved" && (
        <div className="card pad reveal" style={{ marginBottom: 18 }}>
          <div className="row" style={{ gap: 10, marginBottom: 10 }}><Icon.spark width={16} style={{ color: "var(--accent-ink)" }} /><span className="kicker">Studio mind</span></div>
          <div className="row wrap" style={{ gap: 10 }}>
            <input className="input" value={guidance} onChange={(e) => setGuidance(e.target.value)} placeholder="Guide the draft — e.g. warm coastal home, family of four, emphasise natural light…" style={{ flex: 1, minWidth: 260 }} onKeyDown={(e) => e.key === "Enter" && aiDraft()} />
            <button className="btn accent" onClick={() => aiDraft()} disabled={aiBusy}><Icon.spark width={15} />{aiBusy ? "Drafting…" : "Draft with studio mind"}</button>
            <button className="btn soft" onClick={() => setScanOpen(true)}><Icon.brief width={15} />Upload existing brief</button>
          </div>
          <div className="dim" style={{ fontSize: 12, marginTop: 8 }}>Fill in what you know — the studio mind infers the rest from your notes, the project facts and anything you've already written.</div>
        </div>
      )}
      {aiMsg && <div className="card reveal" style={{ padding: "12px 16px", marginBottom: 18, fontSize: 13.5, borderColor: "color-mix(in srgb, var(--accent) 40%, var(--line))" }}>{aiMsg}</div>}

      {/* the approved brief as the design-stage checklist + space audit */}
      <BriefChecklistPanel p={p} canEdit={canEdit} by={session?.name ?? "Studio"} />
      {p.brief.status === "approved" && p.briefChecklist && (
        <div className="row reveal" style={{ justifyContent: "flex-end", marginTop: -8, marginBottom: 18 }}>
          <button className="btn ghost sm" onClick={() => void downloadSpaceReportPdf(p)}><Icon.report width={14} />Download the space report (PDF)</button>
        </div>
      )}

      {/* approval banners */}
      {b.status === "approved" && (
        <div className="card reveal" style={{ padding: "18px 22px", marginBottom: 20, borderColor: "color-mix(in srgb, var(--ok) 40%, var(--line))", background: "color-mix(in srgb, var(--ok) 6%, var(--card))" }}>
          <div className="row" style={{ gap: 12 }}><Icon.checkCircle width={22} style={{ color: "var(--ok)", flexShrink: 0 }} /><div><div style={{ fontWeight: 600 }}>Approved by {b.approvedBy} · {fmtDate(b.approvedOn)}</div>{b.clientNote && <div className="muted" style={{ marginTop: 4, fontStyle: "italic" }}>“{b.clientNote}”</div>}<div style={{ marginTop: 8 }}><RecordBadge p={p} kind="brief-approved" title="Design brief — approved" by={b.approvedBy ?? p.client.name} compact /></div></div></div>
        </div>
      )}
      {b.status === "changes" && b.clientNote && (
        <div className="card reveal" style={{ padding: "18px 22px", marginBottom: 20, borderColor: "color-mix(in srgb, var(--warn) 40%, var(--line))" }}>
          <div className="kicker" style={{ color: "var(--warn)" }}>Changes requested</div><div style={{ marginTop: 6 }}>“{b.clientNote}”</div>
        </div>
      )}

      {/* narrative sections */}
      <div className="col reveal" style={{ gap: 14 }}>
        {(editing ? draft : b.sections).map((s, i) => (
          <div key={s.id} className="card pad">
            <div className="row" style={{ gap: 14, alignItems: "flex-start" }}>
              <div className="serif" style={{ fontSize: 26, color: "var(--ink-3)", lineHeight: 1, width: 34, flexShrink: 0 }}>{String(i + 1).padStart(2, "0")}</div>
              <div style={{ flex: 1 }}>
                {editing ? (
                  <>
                    <input className="input" value={s.title} placeholder="Section title" onChange={(e) => setDraft((d) => d.map((x) => x.id === s.id ? { ...x, title: e.target.value } : x))} style={{ fontWeight: 600, marginBottom: 8 }} />
                    <textarea className="textarea" value={s.body} placeholder={s.hint} onChange={(e) => setDraft((d) => d.map((x) => x.id === s.id ? { ...x, body: e.target.value } : x))} />
                  </>
                ) : (
                  <>
                    <div className="kicker">{s.title}</div>
                    {s.body?.trim() ? <p style={{ marginTop: 8, fontSize: 15.5, lineHeight: 1.6 }}>{s.body}</p> : <p className="dim" style={{ marginTop: 8, fontSize: 14, fontStyle: "italic" }}>{s.hint || "Not yet filled in."}</p>}
                  </>
                )}
              </div>
              {editing && (
                <div className="col" style={{ gap: 5, flexShrink: 0 }}>
                  <button className="iconbtn" style={{ width: 30, height: 30, color: "var(--accent-ink)", borderColor: "var(--accent)" }} disabled={sectionBusy === s.id} title="Draft this section with the studio mind" onClick={() => draftSection(s.id)}>{sectionBusy === s.id ? <span style={{ fontSize: 11 }}>…</span> : <Icon.spark width={15} />}</button>
                  <button className="iconbtn" style={{ width: 30, height: 30 }} disabled={i === 0} title="Move up" onClick={() => moveSection(s.id, -1)}><Icon.caretUp width={15} /></button>
                  <button className="iconbtn" style={{ width: 30, height: 30 }} disabled={i === draft.length - 1} title="Move down" onClick={() => moveSection(s.id, 1)}><Icon.caretDown width={15} /></button>
                  <button className="iconbtn" style={{ width: 30, height: 30 }} title="Remove section" onClick={() => removeSection(s.id)}><Icon.close width={14} /></button>
                </div>
              )}
            </div>
          </div>
        ))}

        {/* section builder */}
        {editing && (
          <div className="panel" style={{ padding: 16, borderStyle: "dashed" }}>
            <div className="row between wrap" style={{ gap: 12 }}>
              <button className="btn soft sm" onClick={() => addSection()}><Icon.plus width={15} />Add section</button>
              {suggestions.length > 0 && (
                <div className="row wrap" style={{ gap: 6, alignItems: "center" }}>
                  <span className="dim" style={{ fontSize: 12 }}>Quick add:</span>
                  {suggestions.map((t) => (
                    <button key={t.title} className="chip" style={{ fontSize: 12 }} onClick={() => addSection(t.title, t.hint)}>+ {t.title}</button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* space program / accommodation schedule */}
      <div className="row between reveal" style={{ margin: "30px 0 6px" }}>
        <div><div className="kicker">Accommodation schedule</div><h3 className="serif" style={{ fontSize: 22, marginTop: 4 }}>Required spaces</h3></div>
        <div className="row wrap" style={{ gap: 8 }}>
          {canEdit && (
            <div className="row" style={{ gap: 4, background: "var(--paper-2)", padding: 3, borderRadius: 100, border: "1px solid var(--line)" }} title="Drag a space's slider to scale it">
              <button className="chip" style={{ fontSize: 11.5, border: "none", background: scaleMode === "lock" ? "var(--ink)" : "transparent", color: scaleMode === "lock" ? "var(--paper)" : "var(--ink-2)" }} onClick={() => setScaleMode("lock")}>Lock total</button>
              <button className="chip" style={{ fontSize: 11.5, border: "none", background: scaleMode === "grow" ? "var(--ink)" : "transparent", color: scaleMode === "grow" ? "var(--paper)" : "var(--ink-2)" }} onClick={() => setScaleMode("grow")}>Grow total</button>
            </div>
          )}
          {canEdit && (
            <select className="select" style={{ width: "auto", padding: "7px 10px", fontSize: 13 }} value={unit} onChange={(e) => actions.setAreaUnit(p.id, e.target.value as any)}>
              <option value="sqft">ft²</option><option value="sqm">m²</option>
            </select>
          )}
          {canEdit && <button className="btn ghost sm" onClick={() => actions.suggestSpaces(p.id)}><Icon.spark width={14} />Suggest from type</button>}
        </div>
      </div>
      <p className="muted reveal" style={{ fontSize: 13.5, marginBottom: 12 }}>Researched for a {p.type.toLowerCase()} — sized to space-planning standards and zoned. Drag a space's slider to scale it — <b>{scaleMode === "lock" ? "Lock total redistributes the space across the others" : "Grow total adds to the gross area"}</b>.</p>

      {(() => {
        const rows = work ?? spaces;
        const cols = canEdit ? "1fr 116px 50px 78px 92px 34px" : "1fr 108px 50px 78px 92px";
        const gross = totalArea(rows);
        const byZone = ZONES.map((z) => ({ z, area: rows.filter((s) => (s.zone ?? "Public") === z).reduce((n, s) => n + s.qty * s.area, 0) })).filter((x) => x.area > 0);
        const commit = () => { if (work) { actions.setBriefSpaces(p.id, work); setWork(null); } };
        const drag = (id: string, v: number) => setWork(scaleSpaces(rows, id, v, scaleMode === "lock"));
        return (
          <div className="card reveal" style={{ overflow: "hidden" }}>
            <div style={{ display: "grid", gridTemplateColumns: cols, gap: 8, padding: "12px 18px", borderBottom: "1px solid var(--line)" }}>
              <span className="kicker" style={{ fontSize: 9.5 }}>Space</span>
              <span className="kicker" style={{ fontSize: 9.5 }}>Zone</span>
              <span className="kicker" style={{ fontSize: 9.5, textAlign: "center" }}>Qty</span>
              <span className="kicker" style={{ fontSize: 9.5, textAlign: "right" }}>Area</span>
              <span className="kicker" style={{ fontSize: 9.5, textAlign: "right" }}>Subtotal</span>
              {canEdit && <span />}
            </div>
            {rows.length === 0 && <div className="dim" style={{ padding: 22, textAlign: "center", fontSize: 13.5 }}>No spaces yet. {canEdit && "Use “Suggest from type” or Draft with studio mind."}</div>}
            {rows.map((s) => (
              <div key={s.id} style={{ padding: "9px 18px", borderBottom: "1px solid var(--line)" }}>
                <div style={{ display: "grid", gridTemplateColumns: cols, gap: 8, alignItems: "center" }}>
                  <div style={{ minWidth: 0 }}>
                    {canEdit ? <input className="input" style={{ padding: "7px 10px", fontSize: 13.5 }} value={s.name} onChange={(e) => updSpace(s.id, { name: e.target.value })} /> : <span style={{ fontSize: 14, fontWeight: 500 }}>{s.name}</span>}
                    {s.note && <div className="dim" style={{ fontSize: 11, marginTop: 3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{s.note}</div>}
                  </div>
                  {canEdit
                    ? <select className="select" style={{ padding: "6px 6px", fontSize: 12 }} value={s.zone ?? "Public"} onChange={(e) => updSpace(s.id, { zone: e.target.value as any })}>{ZONES.map((z) => <option key={z} value={z}>{z}</option>)}</select>
                    : <span className="badge" style={{ fontSize: 10, color: ZONE_COLOR[s.zone ?? "Public"], borderColor: ZONE_COLOR[s.zone ?? "Public"] }}>{s.zone ?? "Public"}</span>}
                  {canEdit ? <input className="input" type="number" style={{ padding: "7px 4px", fontSize: 13, textAlign: "center" }} value={s.qty} onChange={(e) => updSpace(s.id, { qty: Number(e.target.value) })} /> : <span style={{ textAlign: "center", fontSize: 13 }} className="mono">{s.qty}</span>}
                  {canEdit ? <input className="input mono" type="number" style={{ padding: "7px 6px", fontSize: 13, textAlign: "right" }} value={s.area} onChange={(e) => updSpace(s.id, { area: Number(e.target.value) })} /> : <span style={{ textAlign: "right" }} className="mono">{s.area}</span>}
                  <span className="mono" style={{ textAlign: "right", fontSize: 13.5 }}>{(s.qty * s.area).toLocaleString()}</span>
                  {canEdit && <button className="iconbtn" style={{ width: 28, height: 28 }} onClick={() => setSpaces(spaces.filter((x) => x.id !== s.id))}><Icon.close width={13} /></button>}
                </div>
                {canEdit && (
                  <input type="range" min={20} max={Math.max(300, Math.round(s.area * 2))} step={Math.max(5, Math.round(s.area / 50))} value={s.area}
                    onChange={(e) => drag(s.id, Number(e.target.value))} onMouseUp={commit} onTouchEnd={commit} onPointerUp={commit} onKeyUp={commit} onBlur={commit}
                    style={{ width: "100%", marginTop: 7, accentColor: ZONE_COLOR[s.zone ?? "Public"], cursor: "ew-resize" }} />
                )}
              </div>
            ))}
            <div className="row between" style={{ padding: "14px 18px", background: "var(--paper-2)" }}>
              {canEdit ? <button className="btn ghost sm" onClick={() => setSpaces([...spaces, newSpace()])}><Icon.plus width={14} />Add space</button> : <span />}
              <div className="row" style={{ gap: 24 }}>
                <span className="dim" style={{ fontSize: 12.5 }}>{spaces.reduce((n, s) => n + s.qty, 0)} rooms</span>
                <span style={{ fontWeight: 600 }}>Gross <span className="serif" style={{ fontSize: 18 }}>{gross.toLocaleString()} {unitLabel(unit)}</span></span>
              </div>
            </div>

            {/* distribution by zone */}
            {byZone.length > 0 && (
              <div style={{ padding: "14px 18px", borderTop: "1px solid var(--line)" }}>
                <div className="kicker" style={{ fontSize: 9.5, marginBottom: 8 }}>Distribution by zone</div>
                <div style={{ display: "flex", height: 8, borderRadius: 8, overflow: "hidden", background: "var(--line)" }}>
                  {byZone.map((x) => <div key={x.z} title={`${x.z} · ${Math.round(x.area / gross * 100)}%`} style={{ flex: x.area, background: ZONE_COLOR[x.z] }} />)}
                </div>
                <div className="row wrap" style={{ gap: 14, marginTop: 10 }}>
                  {byZone.map((x) => (
                    <span key={x.z} className="row" style={{ gap: 6, fontSize: 11.5 }}><span style={{ width: 9, height: 9, borderRadius: 3, background: ZONE_COLOR[x.z] }} /><span className="dim">{x.z}</span><span className="mono">{Math.round(x.area / gross * 100)}%</span></span>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* references */}
      <div className="row between reveal" style={{ margin: "30px 0 14px" }}>
        <div><div className="kicker">References & precedents</div><h3 className="serif" style={{ fontSize: 22, marginTop: 4 }}>Design references</h3></div>
        {canEdit && <button className="btn ghost sm" onClick={() => setRefOpen(true)}><Icon.plus width={14} />Add reference</button>}
      </div>
      {refs.length === 0 ? (
        <div className="panel reveal" style={{ padding: 30, textAlign: "center", color: "var(--ink-3)", borderStyle: "dashed" }}>No references yet. {canEdit ? "Add precedents, or share the intake link so the client can." : ""}</div>
      ) : (
        <div className="reveal" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px,1fr))", gap: 16 }}>
          {refs.map((r) => (
            <div key={r.id} className="card hoverable" style={{ overflow: "hidden" }}>
              <Plate hue={r.hue} h={120} />
              <div style={{ padding: 14 }}>
                <div className="row between"><div style={{ fontWeight: 600, fontSize: 14 }}>{r.title}</div>{canEdit && <button className="iconbtn" style={{ width: 26, height: 26 }} onClick={() => actions.removeReference(p.id, r.id)}><Icon.close width={13} /></button>}</div>
                {r.note && <div className="muted" style={{ fontSize: 12.5, marginTop: 4 }}>{r.note}</div>}
                <div className="row between" style={{ marginTop: 8 }}>{r.url ? <a href={r.url} target="_blank" rel="noreferrer" className="row" style={{ gap: 5, fontSize: 12, color: "var(--accent-ink)" }}><Icon.arrowUpRight width={13} />View</a> : <span />}{r.addedBy === "client" && <span className="badge" style={{ fontSize: 10 }}>from client</span>}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="dim" style={{ marginTop: 26, fontSize: 13, textAlign: "center" }}>Last updated {fmtDate(b.updatedOn)}</div>
      <AddReference open={refOpen} onClose={() => setRefOpen(false)} onAdd={(r) => { actions.addReference(p.id, r, "Studio"); setRefOpen(false); }} />
      <PdfPreview open={pdfOpen} onClose={() => setPdfOpen(false)} title="Design Brief" filename={`${p.name.replace(/[^\w]+/g, "_")}_brief.pdf`} build={() => briefDoc(p)} />

      <Modal open={scanOpen} onClose={() => setScanOpen(false)} title="Upload an existing brief" wide>
        <div className="col" style={{ gap: 14 }}>
          <p className="muted" style={{ fontSize: 14 }}>Already have a brief or the client's notes? Upload the document — a <b>PDF, image, or text file</b> — or paste the text below. The AI reads it in full and structures it into the brief sections and a space program.</p>

          {/* upload */}
          <div className="panel row between" style={{ padding: "12px 14px", borderStyle: "dashed", gap: 12 }}>
            <div className="row" style={{ gap: 10, minWidth: 0 }}>
              <Icon.download width={17} style={{ color: "var(--accent-ink)", flexShrink: 0 }} />
              <span style={{ fontSize: 13.5, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{scanFileName || "PDF, image, or .txt — the AI reads it directly"}</span>
            </div>
            <button className="btn soft sm" onClick={() => scanFileRef.current?.click()}><Icon.download width={14} />Upload a file</button>
            <input ref={scanFileRef} type="file" accept=".pdf,.txt,.md,.csv,image/*,application/pdf,text/plain" style={{ display: "none" }} onChange={(e) => { onScanFile(e.target.files?.[0] ?? null); if (scanFileRef.current) scanFileRef.current.value = ""; }} />
          </div>
          {scanFile && <div className="badge accent" style={{ alignSelf: "flex-start" }}><Icon.check width={12} />File ready — will be read by the AI</div>}

          <div className="row" style={{ gap: 10, alignItems: "center" }}><div className="divider spread" /><span className="dim" style={{ fontSize: 11.5 }}>or paste text</span><div className="divider spread" /></div>
          <textarea className="textarea" style={{ minHeight: 150 }} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Paste the client's brief or notes here…" />
          <div className="row between">
            <span className="dim" style={{ fontSize: 12.5 }}>This overwrites section content with the structured result.</span>
            <div className="row" style={{ gap: 10 }}><button className="btn ghost" onClick={() => setScanOpen(false)}>Cancel</button><button className="btn accent" onClick={runScan} disabled={!notes.trim() && !scanFile}><Icon.spark width={15} />Scan &amp; structure</button></div>
          </div>
        </div>
      </Modal>
    </Page>
  );
}

function Fact({ label, value, icon }: { label: string; value: string; icon?: "pin" }) {
  return (
    <div>
      <div className="kicker" style={{ fontSize: 9.5, marginBottom: 5 }}>{label}</div>
      <div className="row" style={{ gap: 6, fontWeight: 500 }}>{icon === "pin" && <Icon.pin width={14} style={{ color: "var(--accent-ink)" }} />}<span>{value}</span></div>
    </div>
  );
}

export function AddReference({ open, onClose, onAdd }: { open: boolean; onClose: () => void; onAdd: (r: { title: string; url?: string; note?: string }) => void }) {
  const [f, setF] = useState({ title: "", url: "", note: "" });
  function add() { if (!f.title.trim()) return; onAdd({ title: f.title.trim(), url: f.url.trim() || undefined, note: f.note.trim() || undefined }); setF({ title: "", url: "", note: "" }); }
  return (
    <Modal open={open} onClose={onClose} title="Add a design reference">
      <div className="col" style={{ gap: 14 }}>
        <div className="field"><label>Title</label><input className="input" autoFocus value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. Vipp Chimney House — calm palette" /></div>
        <div className="field"><label>Link <span className="dim">(optional)</span></label><input className="input" value={f.url} onChange={(e) => setF({ ...f, url: e.target.value })} placeholder="https://…" /></div>
        <div className="field"><label>Note <span className="dim">(optional)</span></label><textarea className="textarea" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="What you like about it…" /></div>
        <div className="row" style={{ justifyContent: "flex-end", gap: 10 }}><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn accent" onClick={add}><Icon.plus width={15} />Add</button></div>
      </div>
    </Modal>
  );
}
